/**
 * Quick-add de las tarjetas Paty's con animación "volar a la bolsa".
 *
 * UX: al pulsar el botón de la tarjeta, una copia de la imagen del producto vuela
 * hacia el icono del carrito en la cabecera (feedback instantáneo, enmascara la
 * latencia de red). NO se abre el cart drawer. El contador del carrito se actualiza
 * de forma optimista vía CartLinesUpdateEvent (Horizon refresca el badge) y luego se
 * confirma con /cart.js.
 *
 * Mejora progresiva: si este módulo no carga, el <form> hace un POST normal.
 * Respeta prefers-reduced-motion (sin vuelo, solo el rebote del icono).
 * El listener es delegado en document y el módulo se evalúa una sola vez por
 * documento (los módulos ES se deduplican por URL).
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function cartTarget() {
  return document.querySelector('cart-icon, .header-actions__cart-icon, a[href$="/cart"]');
}

/** Rebote sutil del icono del carrito cuando "aterriza" el producto. */
function bumpCart() {
  const t = cartTarget();
  if (!t || typeof t.animate !== 'function') return;
  t.animate(
    [{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(1)' }],
    { duration: 320, easing: 'cubic-bezier(.34,1.56,.64,1)' }
  );
}

/** Clona la imagen del producto y la anima hacia el icono del carrito. */
function flyToCart(form) {
  try {
    if (REDUCE) { bumpCart(); return; }
    const target = cartTarget();
    const card = form.closest('.paty-card, .paty-packs__card') || form.parentElement;
    const img = card && card.querySelector('img');
    if (!target || !img || typeof img.animate !== 'function') { bumpCart(); return; }

    const src = img.getBoundingClientRect();
    const dst = target.getBoundingClientRect();
    if (!src.width || !dst.width) { bumpCart(); return; }

    const clone = img.cloneNode(true);
    clone.removeAttribute('loading');
    Object.assign(clone.style, {
      position: 'fixed',
      left: src.left + 'px',
      top: src.top + 'px',
      width: src.width + 'px',
      height: src.height + 'px',
      objectFit: 'cover',
      borderRadius: '9999px',
      margin: '0',
      zIndex: '99999',
      pointerEvents: 'none',
      boxShadow: '0 12px 30px rgba(0,0,0,.28)',
      willChange: 'transform, opacity',
    });
    document.body.appendChild(clone);

    const dx = dst.left + dst.width / 2 - (src.left + src.width / 2);
    const dy = dst.top + dst.height / 2 - (src.top + src.height / 2);

    const anim = clone.animate(
      [
        { transform: 'translate(0,0) scale(1)', opacity: 1, offset: 0 },
        { transform: 'translate(' + dx * 0.5 + 'px,' + (dy * 0.5 - 48) + 'px) scale(.55)', opacity: 1, offset: 0.55 },
        { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(.1)', opacity: 0.35, offset: 1 },
      ],
      { duration: 680, easing: 'cubic-bezier(.5,.05,.55,.95)' }
    );
    anim.onfinish = function () { clone.remove(); bumpCart(); };
    anim.oncancel = function () { clone.remove(); };
  } catch (e) {
    bumpCart();
  }
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.classList.contains('paty-card__addform')) return;
  event.preventDefault();
  flyToCart(form);
  addToCart(form);
});

async function addToCart(form) {
  const button = form.querySelector('.paty-card__add, .paty-packs__cta');
  if (button && button.getAttribute('aria-busy') === 'true') return;
  if (button) button.setAttribute('aria-busy', 'true');

  const formData = new FormData(form);

  // Pedir el render de las secciones del carrito para refrescarlas sin recargar.
  const sectionIds = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
  });
  if (sectionIds.size) formData.set('sections', [...sectionIds].join(','));

  const quantity = Number(formData.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();

  // Evento optimista: Horizon actualiza el badge del carrito al instante.
  // Con el ajuste auto_open_cart_drawer activo, este evento también abre el cart drawer.
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(formData.get('id')), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(form.action, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const data = await response.json();

    if (data.status) {
      // Error (p. ej. sin stock): resolver marcando error para no dejar el estado colgado.
      deferred.resolve({ detail: { didError: true, source: 'paty-card' } });
      return;
    }

    const cart = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'paty-card',
        itemCount: quantity,
        sections: data.sections,
        didError: false,
      },
    });

    if (button) {
      button.classList.add('paty-card__add--ok');
      setTimeout(() => button.classList.remove('paty-card__add--ok'), 1600);
    }
  } catch (error) {
    if (typeof deferred.reject === 'function') deferred.reject(error);
    console.warn('[paty-quick-add] add to cart failed:', error);
  } finally {
    if (button) button.removeAttribute('aria-busy');
  }
}
